import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { REASON_LABEL } from './reasons';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { integrityApi } from '@/lib/api/integrity';
import type { AttentionRow } from '@/lib/api/attention';

/**
 * Sending one case to the TPO.
 *
 * The note is required, and that is the entire design. Without it this becomes
 * a button that forwards a machine-generated flag with a student's name on it
 * to someone who cannot ask them about it — which is precisely the thing every
 * other decision in this feature is arranged to prevent.
 *
 * So the field is not optional, not pre-filled, and the evidence sits below it
 * as context rather than above it as the message. What the TPO reads first has
 * to be a person's sentence.
 */
export function RaiseToTpoDialog({
  batchId,
  row,
  onClose,
}: {
  batchId: string;
  row: AttentionRow | null;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');

  // Cleared per student, so a note written about one never travels to the next.
  useEffect(() => {
    setNote('');
  }, [row?.user_id]);

  const raise = useMutation({
    mutationFn: () =>
      integrityApi.raise(batchId, {
        student_id: row!.user_id,
        reason: row!.reason,
        note: note.trim(),
      }),
    onSuccess: () => {
      toast.success(`Raised to your TPO — ${row?.name}`);
      onClose();
    },
    onError: () => toast.error('That could not be sent.'),
  });

  const ready = note.trim().length > 0;

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Raise to your TPO — {row?.name}</DialogTitle>
          <DialogDescription>
            Your TPO can already see every signal here. Raising one tells them{' '}
            <em>you spoke to this student and think they should know</em> — which is a different
            claim, and the only one they should act on.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="raise-note" className="text-xs">
            What you found <span className="text-muted-foreground">(required)</span>
          </Label>
          <Textarea
            id="raise-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="Second time on this assignment. Couldn't explain the routing when I asked."
          />
          {row && (
            <p className="text-[11px] text-muted-foreground">
              Sent with: <span className="font-medium">{REASON_LABEL[row.reason]}</span> ·{' '}
              {row.evidence}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => raise.mutate()} disabled={!ready || raise.isPending}>
            {raise.isPending ? 'Sending…' : 'Raise'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
